import axios from "axios";
import {axiosInstance} from "@/api/axiosInstance";
import {assetsService} from "@/api/assets";
import {clearRoleFromStorage, saveRolesToStorage} from "@/utils/roleStorage";


const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const { domain, clientId, clientSecret } = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN,
    clientId: process.env.REACT_APP_AUTH0_CLIENTID,
    clientSecret: process.env.REACT_APP_AUTH0_CLIENTSECRET,
};

/**
 * Auth Service - Supports both Auth0 and Local Authentication
 */
export const authService = {
    // ==================== LOCAL AUTHENTICATION ====================

    /**
     * Login with email and password (Local Auth)
     */
    login: async (email, password) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                email,
                password,
            });

            const { accessToken, refreshToken, user } = response.data;

            // Store tokens
            localStorage.setItem("access_token", accessToken);
            localStorage.setItem("refresh_token", refreshToken);
            localStorage.setItem("auth_type", "local");
            localStorage.setItem("user", JSON.stringify(user));

            saveRolesToStorage(
                user.roles && user.roles.length ? user.roles : [user.userType],
                user.userType === "COMPANY_USER" ? "business"
                    : user.userType === "ADMIN" ? "admin"
                        : "normal"
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error("Login error:", error);
            return {
                success: false,
                error: error.response?.data?.message || "Login failed. Please check your credentials.",
            };
        }
    },

    /**
     * Register new user (Local Auth)
     */
    signup: async ({ email, password, firstname, lastname, phoneNumber }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
                email,
                password,
                firstname,
                lastname,
                phoneNumber,
            });

            const { accessToken, refreshToken, user } = response.data;

            // Store tokens
            localStorage.setItem("access_token", accessToken);
            localStorage.setItem("refresh_token", refreshToken);
            localStorage.setItem("auth_type", "local");
            localStorage.setItem("user", JSON.stringify(user));

            saveRolesToStorage(
                user.roles && user.roles.length ? user.roles : [user.userType],
                user.userType === "COMPANY_USER" ? "business"
                    : user.userType === "ADMIN" ? "admin"
                        : "normal"
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error("Signup error:", error);
            return {
                success: false,
                error: error.response?.data?.message || "Registration failed. Please try again.",
            };
        }
    },

    signUpCompany: async ({
                              companyName,
                              industry,
                              address,
                              website,
                              logo,
                              numberOfEmployees,
                              firstname,
                              lastname,
                              email,
                              phoneNumber,
                              password,
                          }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/signup-company`, {
                companyName,
                industry,
                address,
                website,
                numberOfEmployees,
                firstname,
                lastname,
                email,
                phoneNumber,
                password,
            });
            if (logo) {
                await assetsService.uploadAsset({
                    file: logo,
                    contentType: "IMAGE",
                    entityType: "COMPANY",
                    entityId: response.data.id,
                    description: "COMPANY_LOGO",
                });
            }
            return response.data;
        } catch (error) {
            console.error("Error creating company:", error.message);
            throw error;
        }
    },

    /**
     * Confirm the 6-digit code emailed at signup, enable the account, and return auth tokens
     */
    verifyEmail: async ({ email, code }) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/verify-email`, {
                email,
                code,
            });

            const { accessToken, refreshToken, user } = response.data;

            localStorage.setItem("access_token", accessToken);
            localStorage.setItem("refresh_token", refreshToken);
            localStorage.setItem("auth_type", "local");
            localStorage.setItem("user", JSON.stringify(user));

            saveRolesToStorage(
                user.roles && user.roles.length ? user.roles : [user.userType],
                user.userType === "COMPANY_USER" ? "business"
                    : user.userType === "ADMIN" ? "admin"
                        : "normal"
            );

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            console.error("Verify email error:", error);
            return {
                success: false,
                error: error.response?.data?.message || "Verification failed. Please try again.",
            };
        }
    },

    /**
     * Issue a fresh verification code for a not-yet-verified account (expires in 30 minutes)
     */
    resendOtp: async (email) => {
        try {
            await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email });
            return { success: true };
        } catch (error) {
            console.error("Resend OTP error:", error);
            return {
                success: false,
                error: error.response?.data?.message || "Failed to resend code. Please try again.",
            };
        }
    },

    /**
     * Refresh access token (Local Auth)
     */
    refreshToken: async () => {
        try {
            const refreshToken = localStorage.getItem("refresh_token");
            if (!refreshToken) {
                throw new Error("No refresh token available");
            }

            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                refreshToken,
            });

            const { accessToken } = response.data;
            localStorage.setItem("access_token", accessToken);

            return {
                success: true,
                accessToken,
            };
        } catch (error) {
            console.error("Token refresh error:", error);
            // Clear tokens on refresh failure
            authService.logout();
            return {
                success: false,
                error: "Session expired. Please login again.",
            };
        }
    },

    /**
     * Change password (Local Auth - Authenticated)
     */
    changePasswordLocal: async (oldPassword, newPassword) => {
        try {
            await axiosInstance.post("/auth/change-password", {
                oldPassword,
                newPassword,
            });
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || "Failed to change password",
            };
        }
    },

    //Change password for the currently connected user
    changeAccountPassword: async (oldPassword, newPassword) => {
        try {
            await axiosInstance.put("/account/password", {
                oldPassword,
                newPassword,
            });
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || "Failed to change password",
            };
        }
    },

    /**
     * Get current user info (Local Auth - Authenticated)
     */
    getCurrentUserLocal: async () => {
        try {
            const response = await axiosInstance.get("/auth/me");
            return response.data;
        } catch (error) {
            console.error("Error fetching current user:", error);
            return null;
        }
    },

    // ==================== AUTH0 AUTHENTICATION ====================

    /**
     * Login with email/password via Auth0
     */
    auth0Login: async (email, password) => {
        try {
            const response = await axios.post(`https://${domain}/oauth/token`, {
                client_id: clientId,
                grant_type: "password",
                username: email,
                password: password,
                audience: `https://${domain}/api/v2/`,
                scope: "openid profile email",
                connection: "Username-Password-Authentication",
            });

            const accessToken = response.data.access_token;
            localStorage.setItem("access_token", accessToken);
            localStorage.setItem("auth_type", "auth0");

            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            return {
                success: false,
                error: error?.response?.data?.error_description || "Login failed",
            };
        }
    },

    /**
     * Signup with email/password via Auth0
     */
    auth0Signup: async (email, password, role = "NORMAL_USER") => {
        try {
            await axios.post(`https://${domain}/dbconnections/signup`, {
                client_id: clientId,
                email,
                password,
                connection: "Username-Password-Authentication",
                user_metadata: {
                    role,
                },
            });
            return { success: true };
        } catch (error) {
            console.log("Auth0 Signup error: ", error);
            return {
                success: false,
                error:
                    error.response?.status === 400
                        ? "Email already used"
                        : error.response?.data?.error_description || error.message,
            };
        }
    },

    /**
     * Forgot password via Auth0
     */
    forgotPassword: async (email) => {
        try {
            const response = await axios.post(`https://${domain}/passwordless/start`, {
                client_id: clientId,
                client_secret: clientSecret,
                connection: "email",
                email,
                send: "link",
                authParams: {
                    redirect_uri: `${process.env.REACT_APP_WEBSITE}/change-password`,
                },
            });
            return response.status === 200;
        } catch (error) {
            console.error("Forgot password error:", error);
            return false;
        }
    },

    /**
     * Change password via Auth0
     */
    changePassword: async (password, verificationToken) => {
        try {
            await axios.post(
                `https://${domain}/api/v2/tickets/password-change`,
                {
                    client_id: clientId,
                    password,
                    token: verificationToken,
                },
                {
                    headers: {
                        Authorization: `Bearer ${verificationToken}`,
                    },
                }
            );
            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    // ==================== COMMON METHODS ====================

    /**
     * Sync user with backend (after Auth0 login)
     */
    syncUser: async () => {
        try {
            const { data } = await axiosInstance.post("/users/sync", {});
            return data;
        } catch (error) {
            console.error("Could not sync user:", error);
            return null;
        }
    },

    /**
     * Get current user profile from backend
     */
    getCurrentUser: async () => {
        try {
            const response = await axiosInstance.get("/users/profile");
            const profile = response.data;

            return {
                id: profile.userId || profile.idpUserId,
                email: profile.email,
                fullName: profile.name,
                firstname: profile.firstname,
                lastname: profile.lastname,
                address: profile.address,
                gender: profile.gender,
                phoneNumber: profile.phoneNumber,
                job: profile.job,
                age: profile.age,
                roles: profile.roles || [],
                authorities: profile.authorities || [],
                permissions: profile.permissions || [],
                userType: profile.userType || "NORMAL_USER",
            };
        } catch (error) {
            console.error("Error fetching user profile:", error);
            // Try to get user from local storage
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                return JSON.parse(storedUser);
            }
            return authService.getUserFromToken();
        }
    },

    /**
     * Extract user info from JWT token
     */
    getUserFromToken: () => {
        try {
            const token = localStorage.getItem("access_token");
            if (!token) return null;

            const base64Url = token.split(".")[1];
            let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            // add missing padding — JWT base64url segments strip trailing '='
            while (base64.length % 4 !== 0) {
                base64 += "=";
            }
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                    .join("")
            );

            const payload = JSON.parse(jsonPayload);

            return {
                id: payload.sub,
                email: payload.email,
                fullName: payload.name,
                firstname: payload.firstname || payload.given_name,
                lastname: payload.lastname || payload.family_name,
                nickname: payload.nickname,
                roles: payload["tale/roles"] || [],
                userType: payload.userType,
            };
        } catch (error) {
            console.error("Error parsing token:", error);
            return null;
        }
    },

    /**
     * Check if user is authenticated
     */
    isAuthenticated: () => {
        const token = localStorage.getItem("access_token");
        if (!token) return false;

        try {
            // Check token expiration
            const base64Url = token.split(".")[1];
            let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            while (base64.length % 4 !== 0) {
                base64 += "=";
            }
            const payload = JSON.parse(atob(base64));

            // Check if token is expired
            if (payload.exp && payload.exp * 1000 < Date.now()) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    },

    /**
     * Get auth type (local or auth0)
     */
    getAuthType: () => {
        return localStorage.getItem("auth_type") || "auth0";
    },

    /**
     * Get stored access token
     */
    getToken: () => {
        return localStorage.getItem("access_token");
    },

    /**
     * Logout user
     */
    logout: () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("auth_type");
        localStorage.removeItem("user");
        clearRoleFromStorage(); // NEW — also clears user_role/user_roles
        localStorage.removeItem("isBusiness");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("isUser");
        window.location.href = "/login";
    },

    /**
     * Logout with backend call (for local auth)
     */
    logoutWithBackend: async () => {
        try {
            const refreshToken = localStorage.getItem("refresh_token");
            if (refreshToken) {
                await axiosInstance.post("/auth/logout", { refreshToken });
            }
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            authService.logout();
        }
    },

};






export default authService;